import { StrictDict } from '../../utils';
import singleSelect from '../images/singleSelect.png';
import multiSelect from '../images/multiSelect.png';
import dropdown from '../images/dropdown.png';
import numericalInput from '../images/numericalInput.png';
import textInput from '../images/textInput.png';
import advancedOlxTemplates from './advancedOlxTemplates';
import basicOlxTemplates from './basicOlxTemplates';
import messages from './messages';

export const ProblemTypeKeys = StrictDict({
  SINGLESELECT: 'multiplechoiceresponse',
  MULTISELECT: 'choiceresponse',
  DROPDOWN: 'optionresponse',
  NUMERIC: 'numericalresponse',
  TEXTINPUT: 'stringresponse',
  ADVANCED: 'advanced',
} as const);
export type ProblemType = typeof ProblemTypeKeys[keyof typeof ProblemTypeKeys];

export const ProblemTypes = StrictDict({
  [ProblemTypeKeys.SINGLESELECT]: {
    intlTitle: messages.problemEditorTypeSingleSelectTitle,
    title: messages.problemEditorTypeSingleSelectTitle.defaultMessage,
    preview: singleSelect,
    intlPreviewDescription: messages.problemEditorTypeSingleSelectPreviewDescription,
    previewDescription: messages.problemEditorTypeSingleSelectPreviewDescription.defaultMessage,
    intlDescription: messages.problemEditorTypeSingleSelectDescription,
    description: messages.problemEditorTypeSingleSelectDescription.defaultMessage,
    helpLink: 'https://edx.readthedocs.io/projects/edx-partner-course-staff/en/latest/exercises_tools/multiple_choice.html',
    prev: ProblemTypeKeys.TEXTINPUT,
    next: ProblemTypeKeys.MULTISELECT,
    template: basicOlxTemplates.singleSelect,

  },
  [ProblemTypeKeys.MULTISELECT]: {
    intlTitle: messages.problemEditorTypeMultiSelectTitle,
    title: messages.problemEditorTypeMultiSelectTitle.defaultMessage,
    preview: multiSelect,
    intlPreviewDescription: messages.problemEditorTypeMultiSelectPreviewDescription,
    previewDescription: messages.problemEditorTypeMultiSelectPreviewDescription.defaultMessage,
    intlDescription: messages.problemEditorTypeMultiSelectDescription,
    description: messages.problemEditorTypeMultiSelectDescription.defaultMessage,
    helpLink: 'https://edx.readthedocs.io/projects/edx-partner-course-staff/en/latest/exercises_tools/checkbox.html',
    next: ProblemTypeKeys.DROPDOWN,
    prev: ProblemTypeKeys.SINGLESELECT,
    template: basicOlxTemplates.multiSelect,
  },
  [ProblemTypeKeys.DROPDOWN]: {
    intlTitle: messages.problemEditorTypeDropDownTitle,
    title: messages.problemEditorTypeDropDownTitle.defaultMessage,
    preview: dropdown,
    intlPreviewDescription: messages.problemEditorTypeDropDownPreviewDescription,
    previewDescription: messages.problemEditorTypeDropDownPreviewDescription.defaultMessage,
    intlDescription: messages.problemEditorTypeDropDownDescription,
    description: messages.problemEditorTypeDropDownDescription.defaultMessage,
    helpLink: 'https://edx.readthedocs.io/projects/edx-partner-course-staff/en/latest/exercises_tools/dropdown.html',
    next: ProblemTypeKeys.NUMERIC,
    prev: ProblemTypeKeys.MULTISELECT,
    template: basicOlxTemplates.dropdown,
  },
  [ProblemTypeKeys.NUMERIC]: {
    intlTitle: messages.problemEditorTypeNumericTitle,
    title: messages.problemEditorTypeNumericTitle.defaultMessage,
    preview: numericalInput,
    intlPreviewDescription: messages.problemEditorTypeNumericPreviewDescription,
    previewDescription: messages.problemEditorTypeNumericPreviewDescription.defaultMessage,
    intlDescription: messages.problemEditorTypeNumericDescription,
    description: messages.problemEditorTypeNumericDescription.defaultMessage,
    helpLink: 'https://edx.readthedocs.io/projects/edx-partner-course-staff/en/latest/exercises_tools/numerical_input.html',
    next: ProblemTypeKeys.TEXTINPUT,
    prev: ProblemTypeKeys.DROPDOWN,
    template: basicOlxTemplates.numeric,
  },
  [ProblemTypeKeys.TEXTINPUT]: {
    intlTitle: messages.problemEditorTypeTextInputTitle,
    title: messages.problemEditorTypeTextInputTitle.defaultMessage,
    preview: textInput,
    intlPreviewDescription: messages.problemEditorTypeTextInputPreviewDescription,
    previewDescription: messages.problemEditorTypeTextInputPreviewDescription.defaultMessage,
    intlDescription: messages.problemEditorTypeTextInputDescription,
    description: messages.problemEditorTypeTextInputDescription.defaultMessage,
    helpLink: 'https://edx.readthedocs.io/projects/edx-partner-course-staff/en/latest/exercises_tools/text_input.html',
    prev: ProblemTypeKeys.NUMERIC,
    next: ProblemTypeKeys.SINGLESELECT,
    template: basicOlxTemplates.textInput,
  },
  [ProblemTypeKeys.ADVANCED]: {
    intlTitle: messages.problemEditorTypeAdvancedTitle,
    title: messages.problemEditorTypeAdvancedTitle.defaultMessage,
    preview: ('<div />'),
    intlDescription: messages.problemEditorTypeAdvancedDescription,
    description: messages.problemEditorTypeAdvancedDescription.defaultMessage,
    helpLink: '#',
  },
});

export const AdvanceProblemKeys = StrictDict({
  BLANK: 'blankadvanced',
  CIRCUITSCHEMATIC: 'circuitschematic',
  JSINPUT: 'jsinputresponse',
  CUSTOMGRADER: 'customgrader',
  IMAGE: 'imageresponse',
  FORMULA: 'formularesponse',
  PROBLEMWITHHINT: 'problemwithhint',
} as const);
export type AdvancedProblemType = typeof AdvanceProblemKeys[keyof typeof AdvanceProblemKeys];

export function isAdvancedProblemType(pt: ProblemType | AdvancedProblemType): pt is AdvancedProblemType {
  return Object.values(AdvanceProblemKeys).includes(pt as any);
}

export const AdvanceProblems = StrictDict({
  [AdvanceProblemKeys.BLANK]: {
    intlTitle: messages.problemEditorTypeAdvancedProbemsBlankTitle,
    title: messages.problemEditorTypeAdvancedProbemsBlankTitle.defaultMessage,
    intlStatus: messages.problemEditorTypeAdvancedProbemsStatusEmpty,
    status: messages.problemEditorTypeAdvancedProbemsStatusEmpty.defaultMessage,
    template: '<problem></problem>',
  },
  [AdvanceProblemKeys.CIRCUITSCHEMATIC]: {
    intlTitle: messages.problemEditorTypeAdvancedProbemsCircuitSchematicTitle,
    title: messages.problemEditorTypeAdvancedProbemsCircuitSchematicTitle.defaultMessage,
    intlStatus: messages.problemEditorTypeAdvancedProbemsStatusNotSupported,
    status: messages.problemEditorTypeAdvancedProbemsStatusNotSupported.defaultMessage,
    template: advancedOlxTemplates.circuitSchematic,
  },
  [AdvanceProblemKeys.JSINPUT]: {
    intlTitle: messages.problemEditorTypeAdvancedProbemsJsInputTitle,
    title: messages.problemEditorTypeAdvancedProbemsJsInputTitle.defaultMessage,
    intlStatus: messages.problemEditorTypeAdvancedProbemsStatusEmpty,
    status: messages.problemEditorTypeAdvancedProbemsStatusEmpty.defaultMessage,
    template: advancedOlxTemplates.jsInputResponse,
  },
  [AdvanceProblemKeys.CUSTOMGRADER]: {
    intlTitle: messages.problemEditorTypeAdvancedProbemsCustomGraderTitle,
    title: messages.problemEditorTypeAdvancedProbemsCustomGraderTitle.defaultMessage,
    intlStatus: messages.problemEditorTypeAdvancedProbemsStatusProvisional,
    status: messages.problemEditorTypeAdvancedProbemsStatusProvisional.defaultMessage,
    template: advancedOlxTemplates.customGrader,
  },
  [AdvanceProblemKeys.IMAGE]: {
    intlTitle: messages.problemEditorTypeAdvancedProbemsImageMappedTitle,
    title: messages.problemEditorTypeAdvancedProbemsImageMappedTitle.defaultMessage,
    intlStatus: messages.problemEditorTypeAdvancedProbemsStatusNotSupported,
    status: messages.problemEditorTypeAdvancedProbemsStatusNotSupported.defaultMessage,
    template: advancedOlxTemplates.imageResponse,
  },
  [AdvanceProblemKeys.FORMULA]: {
    intlTitle: messages.problemEditorTypeAdvancedProbemsFormulaTitle,
    title: messages.problemEditorTypeAdvancedProbemsFormulaTitle.defaultMessage,
    intlStatus: messages.problemEditorTypeAdvancedProbemsStatusEmpty,
    status: messages.problemEditorTypeAdvancedProbemsStatusEmpty.defaultMessage,
    template: advancedOlxTemplates.formulaResponse,
  },
  [AdvanceProblemKeys.PROBLEMWITHHINT]: {
    intlTitle: messages.problemEditorTypeAdvancedProbemsProblemWithHintTitle,
    title: messages.problemEditorTypeAdvancedProbemsProblemWithHintTitle.defaultMessage,
    intlStatus: messages.problemEditorTypeAdvancedProbemsStatusNotSupported,
    status: messages.problemEditorTypeAdvancedProbemsStatusNotSupported.defaultMessage,
    template: advancedOlxTemplates.problemWithHint,
  },
} as const);

export const ShowAnswerTypesKeys = StrictDict({
  ALWAYS: 'always',
  ANSWERED: 'answered',
  ATTEMPTED: 'attempted',
  CLOSED: 'closed',
  FINISHED: 'finished',
  CORRECT_OR_PAST_DUE: 'correct_or_past_due',
  PAST_DUE: 'past_due',
  NEVER: 'never',
  AFTER_SOME_NUMBER_OF_ATTEMPTS: 'after_attempts',
  AFTER_ALL_ATTEMPTS: 'after_all_attempts',
  AFTER_ALL_ATTEMPTS_OR_CORRECT: 'after_all_attempts_or_correct',
  ATTEMPTED_NO_PAST_DUE: 'attempted_no_past_due',
} as const);

export const ShowAnswerTypes = StrictDict({
  [ShowAnswerTypesKeys.ALWAYS]: {
    id: 'authoring.problemeditor.settings.showanswertype.always',
    defaultMessage: 'Always',
  },
  [ShowAnswerTypesKeys.ANSWERED]: {
    id: 'authoring.problemeditor.settings.showanswertype.answered',
    defaultMessage: 'Answered',
  },
  [ShowAnswerTypesKeys.ATTEMPTED]: {
    id: 'authoring.problemeditor.settings.showanswertype.attempted',
    defaultMessage: 'Attempted or Past Due',
  },
  [ShowAnswerTypesKeys.CLOSED]: {
    id: 'authoring.problemeditor.settings.showanswertype.closed',
    defaultMessage: 'Closed',
  },
  [ShowAnswerTypesKeys.FINISHED]: {
    id: 'authoring.problemeditor.settings.showanswertype.finished',
    defaultMessage: 'Finished',
  },
  [ShowAnswerTypesKeys.CORRECT_OR_PAST_DUE]: {
    id: 'authoring.problemeditor.settings.showanswertype.correct_or_past_due',
    defaultMessage: 'Correct or Past Due',
  },
  [ShowAnswerTypesKeys.PAST_DUE]: {
    id: 'authoring.problemeditor.settings.showanswertype.past_due',
    defaultMessage: 'Past Due',
  },
  [ShowAnswerTypesKeys.NEVER]: {
    id: 'authoring.problemeditor.settings.showanswertype.never',
    defaultMessage: 'Never',
  },
  [ShowAnswerTypesKeys.AFTER_SOME_NUMBER_OF_ATTEMPTS]: {
    id: 'authoring.problemeditor.settings.showanswertype.after_attempts',
    defaultMessage: 'After Some Number of Attempts',
  },
  [ShowAnswerTypesKeys.AFTER_ALL_ATTEMPTS]: {
    id: 'authoring.problemeditor.settings.showanswertype.after_all_attempts',
    defaultMessage: 'After All Attempts',
  },
  [ShowAnswerTypesKeys.AFTER_ALL_ATTEMPTS_OR_CORRECT]: {
    id: 'authoring.problemeditor.settings.showanswertype.after_all_attempts_or_correct',
    defaultMessage: 'After All Attempts or Correct',
  },
  [ShowAnswerTypesKeys.ATTEMPTED_NO_PAST_DUE]: {
    id: 'authoring.problemeditor.settings.showanswertype.attempted_no_past_due',
    defaultMessage: 'Attempted',
  },
} as const);

export const RandomizationTypesKeys = StrictDict({
  NEVER: 'never',
  ALWAYS: 'always',
  ONRESET: 'onreset',
  PERSTUDENT: 'per_student',
} as const);

export const RandomizationTypes = StrictDict({
  [RandomizationTypesKeys.ALWAYS]: {
    id: 'authoring.problemeditor.settings.RandomizationTypes.always',
    defaultMessage: 'Always',
  },
  [RandomizationTypesKeys.NEVER]: {
    id: 'authoring.problemeditor.settings.RandomizationTypes.never',
    defaultMessage: 'Never',
  },
  [RandomizationTypesKeys.ONRESET]: {
    id: 'authoring.problemeditor.settings.RandomizationTypes.onreset',
    defaultMessage: 'On Reset',
  },
  [RandomizationTypesKeys.PERSTUDENT]: {
    id: 'authoring.problemeditor.settings.RandomizationTypes.perstudent',
    defaultMessage: 'Per Student',
  },
} as const);

export const RichTextProblems = [ProblemTypeKeys.SINGLESELECT, ProblemTypeKeys.MULTISELECT] as const;

export const settingsOlxAttributes = [
  '@_display_name',
  '@_weight',
  '@_max_attempts',
  '@_showanswer',
  '@_show_reset_button',
  '@_submission_wait_seconds',
  '@_attempts_before_showanswer_button',
] as const;

export const ignoredOlxAttributes = [
  // '@_markdown',  // Not sure if this is safe to ignore; some tests seem to indicate it's not.
  '@_url_name',
  '@_x-is-pointer-node',
] as const;
